import { answerQuestions, checkIsSafeCountry, useCountryList } from "@/hooks/useRequest/useSurveyRequest";
import { AnswerQuestionModel } from "@/interfaces/surveyTypes";
import log2sensors, { SensorsEventIds } from "@/utils/log2sensors";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import SurveyTitle from "../SurveyTitleV2";
import SurveyFooter from "../SurveyFooter";
import SelectArea from "@/components/Select";
import ValidateError from "@/containers/common/ValidateError";
import ContentContainer from "@/containers/common/ContentContainer";
import ConfirmModal from "@/containers/common/ConfirmModal";
import NoticeModalBottom from "@/containers/common/NoticeModalBottom";
import { fetchAppSettings } from "@/service/common.services";
import {
	shouldBlockMainlandAccountOpening,
	shouldBlockMainlandAccountOpeningByIp,
	getIpRegionFromSettings,
	AppSettingItem,
} from "./mainlandWhitelist";

interface NationalityProps {
	answerFieldToValueMap: Map<string, string>;
	questionFieldToIdMap: Map<string, string>;
	ipRegion?: string;
	settings?: AppSettingItem[];
	onNextClick: () => void;
}

interface IFormInput {
	nationality: string;
}

function Nationality(props: NationalityProps) {
	const router = useRouter();
	const { t } = useTranslation("survey");
	const { countryList } = useCountryList({ language: router.locale });
	const [visible, setVisible] = useState(false);
	const [blockedVisible, setBlockedVisible] = useState(false);
	const [blockedMessageKey, setBlockedMessageKey] = useState("mainland-china-account-opening-not-supported");
	const [loading, setLoading] = useState(false);
	const {
		control,
		handleSubmit,
		setValue,
		formState: { isValid, errors },
		getValues,
		trigger,
	} = useForm<IFormInput>({
		defaultValues: {
			nationality: "",
		},
	});

	const catchPropsNationality = props.answerFieldToValueMap?.get("nationality");

	useEffect(() => {
		if (typeof window === "undefined") return;

		if (catchPropsNationality) {
			setValue("nationality", catchPropsNationality, { shouldValidate: true });
		} else {
			setValue("nationality", props.ipRegion || "", { shouldValidate: true });
		}
		trigger("nationality");
	}, [props.ipRegion, catchPropsNationality]);

	const showMainlandBlockedModal = (messageKey: string) => {
		setBlockedMessageKey(messageKey);
		setBlockedVisible(true);
		log2sensors(SensorsEventIds.AO_NATIONALITY_MAINLAND_BLOCKED_VIEW);
	};

	const handleMainlandBlockedConfirm = () => {
		log2sensors(SensorsEventIds.AO_NATIONALITY_MAINLAND_BLOCKED_CONFIRM);
		setBlockedVisible(false);
	};

	const isMainlandIpBlockedByApi = async () => {
		const settings = await fetchAppSettings();
		return shouldBlockMainlandAccountOpeningByIp(getIpRegionFromSettings(settings), settings);
	};

	const onSubmit: SubmitHandler<IFormInput> = async (data) => {
		if (shouldBlockMainlandAccountOpening(data.nationality)) {
			showMainlandBlockedModal("mainland-china-account-opening-not-supported");
			return;
		}
		if (await isMainlandIpBlockedByApi()) {
			showMainlandBlockedModal("cn-restriction-account-opening");
			return;
		}
		// if (data.nationality === "CN") {
		// 	setVisible(true);
		// 	return;
		// }
		sureSubmit(data);
	};

	const sureSubmit = async (data: IFormInput) => {
		try {
			const countryQuestionId = props.questionFieldToIdMap.get("nationality");
			if (!countryQuestionId) {
				throw Error("missing nationality questionId");
			}

			const payload: AnswerQuestionModel[] = [
				{
					answer: data.nationality,
					questionId: countryQuestionId,
				},
			];

			setLoading(true);
			await answerQuestions({ payload });
			props.onNextClick();
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
			log2sensors(SensorsEventIds.AO_NATIONALITY_NEXTPAGE);
		}
	};

	useEffect(() => {
		log2sensors(SensorsEventIds.AO_NATIONALITY_VIEW);
	}, []);

	return (
		<ContentContainer
			className="px-4.5"
			animation
			header={
				<SurveyTitle title={t("what-is-your-nationality")} subtitle={t("ensure-it-aligns-with-your-id-documents")} />
			}
			footer={
				<SurveyFooter
					loading={loading}
					disabled={!isValid}
					onNextBtnClick={handleSubmit(onSubmit)}
					onPrivacyClick={() => log2sensors(SensorsEventIds.AO_NATIONALITY_PRAVICY)}
					onSecureClick={() => log2sensors(SensorsEventIds.AO_NATIONALITY_SECURITY)}
				/>
			}
		>
			<div className="h-12" />
			<Controller
				name="nationality"
				control={control}
				rules={{
					required: t("country-region-cannot-be-empty"),
					validate: {
						checkNationality: async (v) => {
							const isSafeCountry = await checkIsSafeCountry(v);
							return isSafeCountry || t("we-cannot-open-accounts-for-this-country");
						},
					},
				}}
				defaultValue=""
				render={({ field }) => (
					<SelectArea
						{...field}
						placeholder={t("country-region")}
						defaultValue={field.value}
						options={countryList}
						onChange={(value) => {
							field.onChange(value);
							setValue("nationality", value, { shouldValidate: true });
							log2sensors(SensorsEventIds.AO_NATIONALITY_NATIONALITY);
						}}
					/>
				)}
			/>

			<ValidateError error={errors.nationality} />
			<NoticeModalBottom
				visible={blockedVisible}
				onClose={() => setBlockedVisible(false)}
				onOk={handleMainlandBlockedConfirm}
				title={""}
				content={t(blockedMessageKey, { ns: ["survey", "common"] })}
				okText={t("cn-restriction-i-know", { ns: "common" })}
				align="center"
			/>
			<ConfirmModal
				visible={visible}
				onClose={() => setVisible(false)}
				title={t("china-mainland-user-account-opening-notice-title")}
				onCancelText={t("china-mainland-user-account-opening-notice-btn-cancel")}
				onOkText={t("china-mainland-user-account-opening-notice-btn-confirm")}
				onCancel={() => setVisible(false)}
				onOk={() => sureSubmit(getValues())}
			>
				<div className="max-h-80 overflow-y-auto hidden-scroll-bar whitespace-pre-wrap text-left">
					{t("china-mainland-user-account-opening-notice-content")}
				</div>
			</ConfirmModal>
		</ContentContainer>
	);
}

export default Nationality;
